<template>
  <uv-overlay :z-index="9998" :opacity="opacity" :show="showOverlay" @click="handleOverlayClick">
    <view class="c-overlay" :style="computeWrapStyle" @click.stop="void">
      <turn-scale-animate :open="turnScaleAnimate">
        <view class="c-overlay__main">
          <slot />
        </view>

        <view
          v-if="blindBoxClose"
          class="blind-box__close"
          :style="computeCloseStyle"
          @click.stop="handleClose"
        />

        <view
          v-else-if="hasClose"
          class="c-overlay__close"
          :class="`c-overlay__close--${closePosition}`"
          @click.stop="showOverlay = false"
        >
          <uv-icon name="close-circle" color="#ffffff" size="26" />
        </view>

        <template v-if="computeIsMatrix">
          <button-group
            v-for="(item, index) in blindBoxButtonConfig"
            :key="index"
            class="button-group"
            :margins="buttonGroupMargins"
            :item-list="item"
          />
        </template>

        <template v-else>
          <button-group
            v-if="blindBoxButtonConfig.length > 0"
            class="button-group"
            :margins="buttonGroupMargins"
            :item-list="blindBoxButtonConfig"
          />
        </template>

        <slot name="absoluteSlot" />
      </turn-scale-animate>
    </view>
  </uv-overlay>
</template>

<script setup lang="ts">
import TurnScaleAnimate from '@/components/animation-map/turn-scale.vue';
import ButtonGroup from '@/components/button-group/index.vue';
import { CLOSE_EVENT } from '@/constants';
import type { Comp } from '@/types/global-business';
import { computed, onBeforeUnmount, ref, toRefs, watch } from 'vue';
import { useAppStore } from '@/store';

interface Props {
  visible: boolean;
  opacity?: number;
  maskClickAble?: boolean;
  hasClose?: boolean;
  width?: number; // 百分比
  closePosition?: 'bottom' | 'top-right' | 'top-left';
  blindBoxClose?: boolean;
  blindBoxClosePosition?: number[];
  blindBoxButtonConfig?: Comp.ButtonGroupItem[] | Comp.ButtonGroupItem[][];
  buttonGroupMargins?: number[];
  turnScaleAnimate?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  visible: false,
  opacity: 0.65,
  maskClickAble: true,
  hasClose: true,
  width: 75,
  closePosition: 'bottom',
  blindBoxClose: false,
  blindBoxButtonConfig: () => [],
  buttonGroupMargins: () => [],
  turnScaleAnimate: false,
});
const {
  visible,
  opacity,
  maskClickAble,
  hasClose,
  width,
  closePosition,
  blindBoxClose,
  blindBoxButtonConfig,
  blindBoxClosePosition,
  buttonGroupMargins,
} = toRefs(props);

const appStore = useAppStore();

const showOverlay = ref(visible.value);
const wasOpen = ref(false);
const emit = defineEmits([CLOSE_EVENT]);

// 是否二维数组
const computeIsMatrix = computed(() => {
  return (
    Array.isArray(blindBoxButtonConfig.value) &&
    blindBoxButtonConfig.value.length > 0 &&
    Array.isArray(blindBoxButtonConfig.value[0])
  );
});

const computeWrapStyle = computed(() => {
  let style = {};

  if (width.value) {
    style = {
      ...style,
      width: `${width.value}%`,
    };
  }

  return style;
});

const computeCloseStyle = computed(() => {
  let style = {};

  const position = blindBoxClosePosition.value;
  if (position) {
    style = {
      ...style,
      top: `${position[0]}rpx`,
      right: `${position[1]}rpx`,
    };
  }

  return style;
});

const handleOverlayClick = () => {
  if (maskClickAble.value) {
    emit(CLOSE_EVENT);
    showOverlay.value = false;
  }
};

const handleClose = () => {
  emit(CLOSE_EVENT);
};

watch(
  () => visible.value,
  (newVisible, oldVisible) => {
    if (newVisible && !oldVisible) {
      wasOpen.value = true;
      appStore.incrementPopupCount();
    } else if (!newVisible && oldVisible) {
      wasOpen.value = false;
      appStore.decrementPopupCount();
    }
    showOverlay.value = newVisible;
  },
  { immediate: true }
);

/** 组件卸载前，把滚动锁定标识置为false */
onBeforeUnmount(() => {
  if (wasOpen.value) {
    appStore.decrementPopupCount();
  }
});
</script>

<style scoped lang="scss">
.c-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 9999;
  width: 90%;

  .c-overlay__main {
    width: 100%;
    max-height: 80vh;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .c-overlay__close {
    @include scaleTouch;
    position: absolute;

    &.c-overlay__close--bottom {
      bottom: -78rpx;
      left: 50%;
      transform: translateX(-50%);
    }

    &.c-overlay__close--top-right {
      top: -78rpx;
      right: 0;
    }

    &.c-overlay__close--top-left {
      top: -78rpx;
      left: 0;
    }
  }

  .blind-box__close {
    @include scaleTouch;
    position: absolute;
    // top: 10rpx;
    // right: 20rpx;
    top: -40rpx;
    right: -40rpx;
    width: 58rpx;
    height: 58rpx;
    background: url('https://cdn.s3.mall.startcatcher.com/public/blind-box-close.png') no-repeat
      center / cover;
  }

  .button-group {



    width: 100%;
    margin-top: 46rpx;
  }
}
</style>
